import { Link } from 'react-router-dom';
import { ArrowRight, Leaf, HandHeart, Sparkles } from 'lucide-react';
import { Seo } from '@/components/Seo';
import { Container } from '@/components/ui/Container';
import { SectionHeading } from '@/components/ui/SectionHeading';
import { Photo } from '@/components/ui/Photo';
import { VideoHero } from '@/components/ui/VideoHero';
import { SITE } from '@/data/site';
import { FEATURED_IDS } from '@/data/treatments';
import { useTreatments } from '@/lib/useTreatments';
import { ROOMS } from '@/data/accommodation';
import { formatPula } from '@/lib/money';
import { useReveal } from '@/lib/useReveal';
import { useImages } from '@/lib/images';

export function Home() {
  useReveal();
  const img = useImages();
  const { all } = useTreatments();
  const featured = FEATURED_IDS.map((id) => all.find((t) => t.id === id)).filter(
    (t): t is NonNullable<typeof t> => Boolean(t),
  );

  return (
    <>
      <Seo
        title="Grace Haven Spa — Gaborone, Botswana"
        description="A sanctuary of relaxation and a serene BnB in Gaborone. Massage, facials, nails, makeup, and comfortable rooms. Relax. Refresh. Rejuvenate."
      />

      {/* ── Full-screen cinematic hero ───────────────────── */}
      <VideoHero poster={img.heroWide}>
        <Container className="text-center">
          <p className="eyebrow mb-5 text-gold-soft">Spa &amp; BnB · {SITE.city}, {SITE.country}</p>
          <h1 className="mx-auto max-w-4xl font-display text-5xl font-medium leading-[1.04] text-cream sm:text-6xl lg:text-7xl">
            Your <span className="wordmark text-[1.15em] text-gold">haven</span> of relaxation &amp; comfort.
          </h1>
          <div className="rule-gold mx-auto mt-7" />
          <p className="mx-auto mt-7 max-w-xl font-body text-lg font-light leading-relaxed text-cream/85">
            A calm, candle-lit escape in the heart of {SITE.city} — unhurried treatments by day, a
            restful stay by night.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <Link to="/booking" className="btn-gold">Book a treatment <ArrowRight size={16} /></Link>
            <Link to="/stay" className="btn-ghost-light">Stay with us</Link>
          </div>
        </Container>
      </VideoHero>

      {/* ── Two equal pillars: Spa & Stay (Saxon-style) ──── */}
      <section className="bg-cream py-24">
        <Container>
          <SectionHeading
            eyebrow="Two ways to unwind"
            title="The Spa & The Stay"
            center
            intro="Grace Haven is both a day spa and a self-catering BnB — come for an hour of calm, or make a whole weekend of it."
          />
          <div className="mt-14 grid gap-6 md:grid-cols-2">
            {[
              {
                tag: 'The Spa', title: 'Treatments & beauty', img: img.spaPillar,
                text: 'Massage, facials, nails, and makeup — tailored and unhurried.',
                to: '/services', cta: 'Explore treatments',
              },
              {
                tag: 'The Stay', title: 'Rooms & comfort', img: img.stayPillar,
                text: 'Self-catering rooms with a pool, garden, fast WiFi, and a spa discount.',
                to: '/stay', cta: 'Explore the stay',
              },
            ].map((p) => (
              <article key={p.tag} className="reveal group relative overflow-hidden">
                <Photo src={p.img} alt={p.title} className="aspect-[4/3] w-full" imgClassName="transition duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-espresso-deep/90 via-espresso-deep/30 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-8">
                  <p className="eyebrow text-gold-soft">{p.tag}</p>
                  <h3 className="mt-2 font-display text-3xl text-cream">{p.title}</h3>
                  <p className="mt-2 max-w-sm font-body text-sm leading-relaxed text-cream/80">{p.text}</p>
                  <Link to={p.to} className="btn-gold mt-5 !px-6 !py-2.5">{p.cta} <ArrowRight size={15} /></Link>
                </div>
              </article>
            ))}
          </div>
        </Container>
      </section>

      {/* ── Philosophy ───────────────────────────────────── */}
      <section className="bg-paper py-24">
        <Container>
          <div className="grid items-center gap-14 lg:grid-cols-2">
            <Photo src={img.philosophy} alt="A serene treatment room" className="reveal aspect-[4/5] rounded-sm shadow-soft" />
            <div>
              <SectionHeading
                eyebrow="Our philosophy"
                title="Rest is not a luxury. It is care."
                intro="Every visit is unhurried and tailored — from the warmth of the towels to the pressure of the touch. We keep our days uncrowded so you are truly looked after."
              />
              <div className="mt-10 grid gap-8 sm:grid-cols-3">
                {[
                  { icon: Leaf, title: 'Natural', text: 'Botanical oils and skin-kind products.' },
                  { icon: HandHeart, title: 'Skilled', text: 'Experienced therapists who listen first.' },
                  { icon: Sparkles, title: 'Serene', text: 'Calm rooms that quiet a busy mind.' },
                ].map((f) => (
                  <div key={f.title} className="reveal">
                    <f.icon className="text-gold-deep" size={26} strokeWidth={1.4} />
                    <h3 className="mt-3 font-display text-xl text-espresso">{f.title}</h3>
                    <p className="mt-1.5 font-body text-sm leading-relaxed text-muted">{f.text}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* ── Featured treatments (spa depth) ──────────────── */}
      <section className="bg-cream py-24">
        <Container>
          <SectionHeading eyebrow="From the spa menu" title="Most-loved treatments" center
            intro="A taste of the menu. Explore the full range, then book the moment that's calling you." />
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {featured.map((t) => (
              <article key={t.id} className="reveal group flex flex-col border border-line bg-paper p-7 transition hover:shadow-soft">
                <div className="flex-1">
                  <h3 className="font-display text-2xl text-espresso">{t.name}</h3>
                  <div className="rule-gold mt-3" />
                </div>
                <div className="mt-6 flex items-end justify-between border-t border-line pt-4">
                  <span className="font-body text-xs uppercase tracking-wide text-muted">{t.durationMin ? `${t.durationMin} min` : 'Treatment'}</span>
                  <span className="font-display text-xl text-espresso">{formatPula(t.price)}</span>
                </div>
              </article>
            ))}
          </div>
          <div className="mt-12 text-center"><Link to="/services" className="btn-outline">See all treatments</Link></div>
        </Container>
      </section>

      {/* ── Stay highlights (stay depth — equal weight) ──── */}
      <section className="bg-paper py-24">
        <Container>
          <SectionHeading eyebrow="From the BnB" title="Stay the night" center
            intro="Self-catering rooms beside the spa — perfect for a wellness weekend." />
          <div className="mx-auto mt-14 grid max-w-3xl gap-6 sm:grid-cols-2">
            {ROOMS.map((r) => (
              <article key={r.id} className="reveal flex flex-col border border-line bg-cream p-8">
                <h3 className="font-display text-2xl text-espresso">{r.name}</h3>
                <div className="rule-gold mt-3" />
                <p className="mt-4 flex-1 font-body text-sm leading-relaxed text-muted">{r.blurb}</p>
                <div className="mt-6 flex items-end justify-between border-t border-line pt-4">
                  <span className="font-display text-2xl text-espresso">{formatPula(r.price)}</span>
                  <span className="font-body text-xs uppercase tracking-wide text-muted">per night</span>
                </div>
              </article>
            ))}
          </div>
          <div className="mt-12 text-center"><Link to="/stay" className="btn-outline">Explore the stay</Link></div>
        </Container>
      </section>

      {/* ── Quiet quote ──────────────────────────────────── */}
      <section className="bg-espresso py-24 text-center">
        <Container>
          <p className="mx-auto max-w-3xl font-display text-3xl font-medium italic leading-relaxed text-cream sm:text-4xl">
            “Relax. Refresh. Rejuvenate. You deserve it.”
          </p>
          <div className="rule-gold mx-auto mt-8" />
        </Container>
      </section>

      {/* ── CTA band ─────────────────────────────────────── */}
      <section className="relative overflow-hidden bg-espresso-deep py-20">
        <Photo src={img.ctaBand} alt="" className="absolute inset-0" imgClassName="opacity-25" />
        <div className="absolute inset-0 bg-espresso-deep/70" />
        <Container className="relative z-10 text-center">
          <h2 className="font-display text-3xl text-cream sm:text-4xl">Ready for some quiet?</h2>
          <p className="mx-auto mt-3 max-w-md font-body text-cream/75">Book a treatment or enquire about a stay — we'll confirm by email.</p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Link to="/book" className="btn-gold">Book Now <ArrowRight size={16} /></Link>
            <Link to="/stay" className="btn-ghost-light">Stay with us</Link>
          </div>
        </Container>
      </section>
    </>
  );
}
